import React, {Component} from 'react';
import '@amzn/awsui-global-styles/polaris.css';
import {
    ColumnLayout,
    Button,
    Form,
    FormField,
    Modal,
    Container,
    Header,
    SpaceBetween,
    Box,
    Input,
    ExpandableSection,
} from "@amzn/awsui-components-react";
import CONSTANT from "../utils/constant";
import { API_TO_DISPLAY_MAPPING_SETTLEMENT_STATE_CHANGES } from "../utils/constant";
import APIClient from "../utils/apiClient";
import Logger from "../utils/logger";
import {
    PAGE_SELECTOR_OPTIONS_SETTLEMENT,
    PAGE_SIZE,
    REQUIRED_COLUMNS_SETTLEMENT_STATE_CHANGES
} from "../config/table";
import FormatData from "../utils/formatData";
import DataContentTable from '../component/dataContentTable';
import {PolarisV3FlashBar} from "../component/dataDisplay";

/*
 * Helper function to check if a header is a date column
 */
const isDateColumn = (displayHeader) => {
    return displayHeader === CONSTANT.PR_COL_APPROVAL_DATE || 
           displayHeader === CONSTANT.PR_COL_CREATION_DATE || 
           displayHeader === CONSTANT.PR_COL_UPDATE_DATE || 
           displayHeader === CONSTANT.PR_COL_REJECTION_DATE ||
           displayHeader === CONSTANT.PR_COL_EXPORT_DATE ||
           displayHeader === CONSTANT.PR_COL_CLOSE_DATE ||
           displayHeader === CONSTANT.PR_COL_VALUE_DATE ||
           displayHeader === CONSTANT.PR_COL_UPLOAD_DATE ||
           displayHeader === CONSTANT.PR_COL_DUE_DATE;
};

const isStateTimestamp = (displayHeader) => {
    return displayHeader === CONSTANT.SSC_COL_STATE_TIMESTAMP;
};

/*
 * FAQ container for Settlement State Changes page
 */
class PaymentAuditHistoryFaqBlock extends Component {
    render() {
        return (
            <Container>
                <ExpandableSection
                    headerText="FAQ"
                    variant="container"
                >
                    <SpaceBetween direction="vertical" size="m">
                        <div>
                            <Header variant="h3">What is the Settlement ID?</Header>
                            <p>The Settlement ID is a computer-generated unique ID that exists for every settlement entity (pending or historical) in TRS.</p>
                        </div>
                        <div>
                            <Header variant="h3">How to obtain the Settlement ID?</Header>
                            <p>
                                Locate the settlement of interest in Settlement Request → Pending Liquidity Checks/Pending Settlements/Historical Settlements. 
                                You can use the Settlement ID to search for the audit history of that settlement.
                            </p>
                        </div>
                    </SpaceBetween>
                </ExpandableSection>
            </Container>
        );
    }
}

/*
 * Display the records in the SettlementStateChange table
 */
class SettlementStateChangeContentBlock extends Component {
    constructor(props) {
        super(props);
        
        const allColumnIds = props.columnDefinitions.map(col => col.id);
        
        this.state = {
            filteringText: '',
            filteringTokens: [],
            pageSize: PAGE_SIZE,
            contentSelectorOptions: this.props.contentSelectorOptions,
            wrapLines: false,
            sortingDescending: true,
            sortingColumn: CONSTANT.SSC_COL_STATE_TIMESTAMP,
        }
        
        Logger.logInfo("All column IDs: " + allColumnIds.join(", "));
    }

    onWrapLinesChange({detail}) {
        this.setState({
            wrapLines: detail.value
        });
    }

    onPropertyFilteringChange({detail}) {
        this.setState({
            filteringTokens: detail.tokens
        });
    }

    clearFilter() {
        this.setState({
            filteringText: '',
            filteringTokens: []
        });
    }

    render() {
        const searchBar =
            <Container>
                <SpaceBetween direction="vertical" size="m">
                    <ColumnLayout columns={3}>
                        <FormField
                            description="Enter a Settlement ID to search for the audit history of that settlement"
                            label="Settlement ID*"
                        >
                            <Input
                                onChange={event => {
                                    this.props.handleValueChange('settlementId', event.detail.value);
                                }}
                                value={this.props.settlementId}
                                placeholder="Enter Settlement ID"
                            />
                        </FormField>
                        <Box float="left">
                            <SpaceBetween direction="vertical" size="xs">
                                <Button variant="primary"
                                        iconName="search"
                                        iconAlign="right"
                                        onClick={() => {
                                            this.props.handleQueryClick();
                                        }}
                                >Search</Button>
                            </SpaceBetween>
                        </Box>
                        <div></div> {/* Empty column for alignment */}
                    </ColumnLayout>
                </SpaceBetween>
            </Container>;

        const tableHeader =
            <SpaceBetween direction="vertical" size="m">
                {this.props.feedbackMessage}
                {searchBar}
            </SpaceBetween>;

        const defaultSortingState = {
            defaultState: {
                sortingColumn: {
                    sortingField: CONSTANT.SSC_COL_STATE_TIMESTAMP,
                    sortingComparator: (a, b) => {
                        const formattedA = FormatData.formatISODateToPSTTimezoneDatePrecise(a[CONSTANT.SSC_COL_STATE_TIMESTAMP]);
                        const formattedB = FormatData.formatISODateToPSTTimezoneDatePrecise(b[CONSTANT.SSC_COL_STATE_TIMESTAMP]);
                        return formattedA.localeCompare(formattedB);
                    }
                },
                isDescending: true
            }
        }

        return (
            <Container header={
                <Header variant="h2">
                    Settlement Audit History
                </Header>
            }>
                <ColumnLayout columns={1}>
                    <DataContentTable
                        wrapLines={false}
                        defaultSortingState={defaultSortingState}
                        columnDefinitions={this.props.columnDefinitions}
                        distributions={this.props.stateChanges || []}
                        header={tableHeader}
                        filterOptions={this.props.filterOptions}
                        trackBy={CONSTANT.SSC_COL_SETTLEMENTSTATEID}
                        pageSelectorOptions={PAGE_SELECTOR_OPTIONS_SETTLEMENT}
                        contentSelectorOptions={this.state.contentSelectorOptions}
                        selectionType={null}
                    />
                </ColumnLayout>
            </Container>);
    }
}

/*
 * Settlement Audit History parent component
 * Fetches state change data for a specific settlement ID
 */
class QueryPaymentAuditHistory extends Component {
    constructor(props) {
        super(props);
        this.state = {
            status: CONSTANT.MODAL_EMPTY,
            statusMsg: "",
            loading: false,
            stateChanges: [],
            settlementId: "",
            userGroup: localStorage.getItem(CONSTANT.USER_GROUP_NAME)
        }
    }

    handleValueChange = (name, value) => {
        this.setState({[name]: value});
    };

    // Transform API response from camelCase keys to display header keys + add change indicator metadata
    transformApiResponseToDisplayFormat = (data) => {
        if (!Array.isArray(data)) return data;
        
        const transformedData = data.map(item => {
            const transformedItem = {};
            Object.keys(item).forEach(apiKey => {
                const displayKey = API_TO_DISPLAY_MAPPING_SETTLEMENT_STATE_CHANGES[apiKey];
                if (displayKey) {
                    transformedItem[displayKey] = item[apiKey];
                } else {
                    transformedItem[apiKey] = item[apiKey];
                }
            });
            return transformedItem;
        });
        
        return this.addChangeIndicators(transformedData);
    };

    // Adds a field _changedFields to each API response entry to store: changed fields compared to the previous entry of the same Settlement ID
    addChangeIndicators = (data) => {
        Logger.logInfo(`[CHANGE DETECTION] Processing ${data.length} records`);
        
        if (!Array.isArray(data) || data.length <= 1) {
            return data;
        }
        
        // Make sure data is sorted by State_Timestamp first
        const sortedData = [...data].sort((a, b) => {
            const dateA = new Date(a[CONSTANT.SSC_COL_STATE_TIMESTAMP]);
            const dateB = new Date(b[CONSTANT.SSC_COL_STATE_TIMESTAMP]);
            return dateA - dateB;
        });
        
        // Map to store changed fields of each settlement state change ( Settlement State ID: {changedFields} )
        const changeMap = new Map();
        for (let i = 1; i < sortedData.length; i++) {
            const currentItem = sortedData[i];
            const previousItem = sortedData[i - 1];
            const changedFields = new Set();
            
            Logger.logInfo(`[CHANGE DETECTION] Comparing record ${i} with ${i-1}:`);
            Logger.logInfo(`[CHANGE DETECTION]   Current: SettlementStateID=${currentItem[CONSTANT.SSC_COL_SETTLEMENTSTATEID]}`);
            Logger.logInfo(`[CHANGE DETECTION]   Previous: SettlementStateID=${previousItem[CONSTANT.SSC_COL_SETTLEMENTSTATEID]}`);
            
            const fieldsToIgnore = [
                CONSTANT.SSC_COL_SETTLEMENTSTATEID,
                CONSTANT.SSC_COL_ACTION,
                CONSTANT.SSC_COL_STATE_TIMESTAMP,
            ];
            
            Object.keys(currentItem).forEach(field => {
                if (!fieldsToIgnore.includes(field)) {
                    const currentValue = String(currentItem[field] || '').trim();
                    const previousValue = String(previousItem[field] || '').trim();
                    
                    if (currentValue !== previousValue) {
                        changedFields.add(field);
                    }
                }
            });

            changeMap.set(currentItem[CONSTANT.SSC_COL_SETTLEMENTSTATEID], changedFields);
        }
        
        const result = data.map(item => ({
            ...item,
            _changedFields: changeMap.get(item[CONSTANT.SSC_COL_SETTLEMENTSTATEID]) || new Set()
        }));
        
        Logger.logInfo(`[CHANGE DETECTION] Change indicators successfully added.`);
        return result;
    };

    handleQuery = () => {
        Logger.logInfo('handleQuery for Settlement Audit History');
        
        if (!this.state.settlementId || this.state.settlementId.trim() === '') {
            this.setState({
                status: CONSTANT.MODAL_QUERY_ERROR,
                statusMsg: "Settlement ID is required"
            });
            return;
        }

        this.setState({
            loading: true,
            status: CONSTANT.MODAL_QUERYING
        });
        
        // Make API call to get Settlement Audit History for the specified Settlement ID
        APIClient.invoke('GET', 'settlement-state-changes/' + this.state.settlementId, {},
            undefined,
            (err, data) => {
                this.setState({loading: false});
                if (!err) {
                    Logger.logInfo("Data retrieved: " + JSON.stringify(data));
                    if (Array.isArray(data)) {
                        Logger.logInfo("Detected array response");
                        // Transform the data from camelCase keys to display header keys
                        const transformedData = this.transformApiResponseToDisplayFormat(data);
                        this.setState({
                            status: CONSTANT.MODAL_UPDATE_SUCCESS,
                            stateChanges: transformedData
                        });
                        Logger.logInfo("After transformation:" + JSON.stringify(this.state.stateChanges))
                    } else {
                        this.setState({
                            status: CONSTANT.MODAL_OTHER_ERROR,
                            statusMsg: `Invalid response format: ${err.message || err.toString() || JSON.stringify(err)}`
                        });
                    }
                } else {
                    Logger.logError(`Error when querying settlement state changes: ${err.message || err.toString() || JSON.stringify(err)}`);
                    this.setState({
                        status: CONSTANT.MODAL_OTHER_ERROR,
                        statusMsg: `System error when querying settlement state changes: ${err.message || err.toString() || JSON.stringify(err)}`
                    });
                }
            });
    }

    recoverAddButtonEmptyState = () => {
        this.setState({
            status: CONSTANT.MODAL_EMPTY
        });
    };

    getFeedbackMessage = () => {
        Logger.logInfo("Modal Message " + this.state.status);
        switch (this.state.status) {
            case CONSTANT.MODAL_QUERYING:
                return <PolarisV3FlashBar 
                    type="success"
                    loading={true}
                    content={<>Fetching settlement audit history... Please wait.</>}
                    dismissible={false}
                    key={this.state.status}
                />;
            case CONSTANT.MODAL_OTHER_ERROR:
                return <PolarisV3FlashBar 
                    type="error"
                    loading={false}
                    header="Error"
                    content={<>{this.state.statusMsg}</>}
                    dismissible={true}
                    onDismiss={this.recoverAddButtonEmptyState}
                    key={this.state.status}
                />;
            case CONSTANT.MODAL_QUERY_ERROR:
                return <PolarisV3FlashBar 
                    type="error"
                    loading={false}
                    header="Query failed"
                    content={<>{this.state.statusMsg}</>}
                    dismissible={true}
                    onDismiss={this.recoverAddButtonEmptyState}
                    key={this.state.status}
                />;
            case CONSTANT.MODAL_SYSTEM_ERROR:
                return <Modal
                    content={<div>Request Failed! {this.state.statusMsg}</div>}
                    visible={true}
                    header="System Error"
                    onDismiss={this.recoverAddButtonEmptyState}
                />;
            default:
                return null;
        }
    };

    render() {
        let columnDefinitions = [];
        let sortableColumns = [];
        let filterOptions = [];
        let contentSelectorOptions = [];
        let visibleColumnOption = [];
        let firstOption = {};
        firstOption['label'] = 'Properties';
        
        const displayHeaders = REQUIRED_COLUMNS_SETTLEMENT_STATE_CHANGES;
        
        displayHeaders.forEach((displayHeader) => {
            let columnDefinitionsItem = {};
            columnDefinitionsItem['id'] = displayHeader;
            columnDefinitionsItem['header'] = displayHeader;

            // Cell renderer with conditional styling for changed fields
            const renderCell = (header) => {
                const CellRenderer = (item) => {
                    const isChanged = item._changedFields && item._changedFields.has(header);
                    
                    let cellContent;
                    
                    // Check for date columns that need special formatting
                    if (isDateColumn(header)) {
                        cellContent = FormatData.formatISODateToPSTTimezoneDate(item[header]);
                    } else if (isStateTimestamp(header)) {
                        cellContent = FormatData.formatISODateToPSTTimezoneDatePrecise(item[header]);
                    } else {
                        cellContent = item[header];
                    }
                    
                    // Conditional styling for changed fields
                    if (isChanged) {
                        return (
                            <div style={{
                                backgroundColor: '#d4edda',
                                padding: '4px 8px',
                                borderRadius: '3px',
                                border: '1px solid #c3e6cb',
                                width: '100%',
                                boxSizing: 'border-box'
                            }}>
                                {cellContent}
                            </div>
                        );
                    }
                    
                    return cellContent;
                };
                
                CellRenderer.displayName = `SettlementAuditCellRenderer_${header.replace(/\s+/g, '_')}`;
                return CellRenderer;
            };

            columnDefinitionsItem['cell'] = renderCell(displayHeader);

            if (isDateColumn(displayHeader)) {
                columnDefinitionsItem['sortingComparator'] = (a, b) => {
                    const formattedA = FormatData.formatISODateToPSTTimezoneDatePrecise(a[displayHeader]);
                    const formattedB = FormatData.formatISODateToPSTTimezoneDatePrecise(b[displayHeader]);
                    return formattedA.localeCompare(formattedB);
                };
            }

            columnDefinitionsItem['width'] = 150;
            columnDefinitionsItem['minWidth'] = '100px';
            columnDefinitions.push(columnDefinitionsItem);
            
            let sortableColumnsItem = {};
            sortableColumnsItem['id'] = displayHeader;
            sortableColumnsItem['field'] = displayHeader;
            sortableColumns.push(sortableColumnsItem);

            let filterColumnOptionItem = {};
            filterColumnOptionItem['propertyLabel'] = displayHeader;
            filterColumnOptionItem['propertyKey'] = displayHeader;
            filterColumnOptionItem['groupValuesLabel'] = displayHeader + " values";
            filterColumnOptionItem['values'] = [];
            filterOptions.push(filterColumnOptionItem);
            
            let contentSelectorItem = {};
            contentSelectorItem['id'] = displayHeader;
            contentSelectorItem['label'] = displayHeader;
            contentSelectorItem['editable'] = true;
            contentSelectorItem['visible'] = true;
            contentSelectorOptions.push(contentSelectorItem);
        });
        
        contentSelectorOptions.forEach(option => {
            option.visible = true;
        });
        
        firstOption['options'] = contentSelectorOptions;
        visibleColumnOption.push(firstOption);
        
        Logger.logInfo("columnDefinitions: " + columnDefinitions.length + " columns defined");
        Logger.logInfo("contentSelectorOptions: " + contentSelectorOptions.length + " options available");
        if (this.state.stateChanges && this.state.stateChanges.length > 0) {
            Logger.logInfo("Sample data keys: " + Object.keys(this.state.stateChanges[0]).join(", "));
        }

        return (
            <div>
                <Form>
                    <br/>
                    <PaymentAuditHistoryFaqBlock />
                    <br/>
                    <SettlementStateChangeContentBlock 
                        stateChanges={this.state.stateChanges}
                        contentSelectorOptions={visibleColumnOption}
                        sortableColumns={sortableColumns}
                        feedbackMessage={this.getFeedbackMessage()}
                        columnDefinitions={columnDefinitions}
                        filterOptions={filterOptions}
                        handleQueryClick={this.handleQuery}
                        handleValueChange={this.handleValueChange}
                        settlementId={this.state.settlementId}
                    />
                </Form>
            </div>);
    }
}

export default QueryPaymentAuditHistory;
